import { APCreditMapping, APExam } from '@/app/types/interfaces';

// Static ESM import works in Next.js and the browser verification bundle.
import apCreditEquivData from './apcredequiv.json';

// Convert the JSON data to the format expected by our application
export const AP_CREDIT_DATA: APCreditMapping = {};

// Process the real AP credit data
try {
  apCreditEquivData.AP_Credit_Equivalencies.forEach((equiv: any) => {
    const examName = equiv.AP_EXAM;
    const scoreRange = equiv.SCORE;
    const rutgersCourses = equiv["RUTGERS_COURSE(S)_GRANTED"]; // Fixed field name
    const totalCredits = equiv.TOTAL_CREDITS;
    const programEquivalency = equiv.Program_Equivalency;

    // Create a unique ID for this exam
    const examId = examName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

    // Parse score requirements
    let minScore = 1;
    let maxScore = 5;
    if (scoreRange.includes('4 or 5')) {
      minScore = 4;
      maxScore = 5;
    } else if (scoreRange === '4') {
      minScore = 4;
      maxScore = 4;
    } else if (scoreRange === '5') {
      minScore = 5;
      maxScore = 5;
    }

    // Parse Rutgers courses
    const courseStrings = rutgersCourses.split(/[&,]/).map((course: string) => course.trim());
    const rutgersEquivalents = courseStrings.map((courseStr: string) => {
      // Extract course code and name
      const match = courseStr.match(/(\d{2}:\d{3}:\d{3})/);
      if (match) {
        const courseCode = match[1];
        return {
          rutgersCourse: courseCode,
          courseName: courseStr,
          credits: totalCredits / courseStrings.length, // Distribute credits evenly
          minScore,
          maxScore,
          notes: programEquivalency
        };
      } else {
        // Handle cases like "Elective Credit (no course equivalency)"
        return {
          rutgersCourse: 'ELECTIVE',
          courseName: courseStr,
          credits: totalCredits,
          minScore,
          maxScore,
          notes: programEquivalency
        };
      }
    });

    // Create or update the exam entry
    if (!AP_CREDIT_DATA[examId]) {
      AP_CREDIT_DATA[examId] = {
        id: examId,
        name: examName,
        subject: getSubjectFromExam(examName),
        description: `${examName} - Score ${scoreRange} required`,
        minScore,
        maxScore,
        rutgersEquivalents
      };
    } else {
      // If exam already exists, add the new score range
      AP_CREDIT_DATA[examId].rutgersEquivalents.push(...rutgersEquivalents);
      if (minScore < AP_CREDIT_DATA[examId].minScore) {
        AP_CREDIT_DATA[examId].minScore = minScore;
      }
      if (maxScore > AP_CREDIT_DATA[examId].maxScore) {
        AP_CREDIT_DATA[examId].maxScore = maxScore;
      }
    }
  });
} catch (error) {
  console.error('Error processing AP credit data:', error);
}

// Helper function to determine subject from exam name
function getSubjectFromExam(examName: string): string {
  // Return 'Other' for all exams to remove classifications
  return 'Other';
}

// Helper functions with error handling and memoization
let cachedAllExams: APExam[] | null = null;
let cachedSubjects: string[] | null = null;

export const getAPExamById = (id: string) => {
  try {
    return AP_CREDIT_DATA[id] || null;
  } catch (error) {
    console.error('Error getting AP exam by ID:', error);
    return null;
  }
};

export const getAllAPExams = () => {
  try {
    if (cachedAllExams) {
      return cachedAllExams;
    }
    cachedAllExams = Object.values(AP_CREDIT_DATA);
    return cachedAllExams;
  } catch (error) {
    console.error('Error getting all AP exams:', error);
    return [];
  }
};

export const getAPExamsBySubject = (subject: string) => {
  try {
    return Object.values(AP_CREDIT_DATA).filter(exam => exam.subject === subject);
  } catch (error) {
    console.error('Error getting AP exams by subject:', error);
    return [];
  }
};

export const getSubjects = () => {
  try {
    if (cachedSubjects) {
      return cachedSubjects;
    }
    const subjects = new Set(Object.values(AP_CREDIT_DATA).map(exam => exam.subject));
    cachedSubjects = Array.from(subjects).sort();
    return cachedSubjects;
  } catch (error) {
    console.error('Error getting subjects:', error);
    return [];
  }
}; 